/* Independent of theme animation libraries: links remain usable before enhancement. */
if (!customElements.get('dope-video-lookbook')) {
  customElements.define('dope-video-lookbook', class extends HTMLElement {
    connectedCallback() {
      this.stopPreviews?.();
      this.abort?.abort();
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.previewObserver?.disconnect();
      const previews = [...this.querySelectorAll('[data-video-preview]')];
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const previewTokens = new WeakMap();
      const stopPreview = video => {
        previewTokens.set(video, (previewTokens.get(video) || 0) + 1);
        if (this.activePreview === video) this.activePreview = null;
        video.pause();
        video.classList.remove('is-playing');
      };
      const stopPreviews = () => previews.forEach(stopPreview);
      const startPreview = async video => {
        if (reduced.matches || navigator.connection?.saveData || document.hidden || this.querySelector('dialog[open]')) return;
        const bounds = video.parentElement.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= innerHeight || bounds.right <= 0 || bounds.left >= innerWidth) return;
        stopPreviews();
        const token = (previewTokens.get(video) || 0) + 1;
        previewTokens.set(video, token);
        this.activePreview = video;
        if (!video.getAttribute('src')) video.src = video.dataset.videoPreview;
        video.muted = true;
        try {
          await video.play();
          if (previewTokens.get(video) !== token) {
            if (this.activePreview !== video) video.pause();
            return;
          }
          video.classList.add('is-playing');
        } catch {
          if (previewTokens.get(video) === token) {
            this.activePreview = null;
            video.classList.remove('is-playing');
          }
        }
      };
      previews.forEach(video => {
        const poster = video.parentElement;
        poster.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') startPreview(video); }, options);
        poster.addEventListener('pointerleave', () => stopPreview(video), options);
        poster.addEventListener('focus', () => startPreview(video), options);
        poster.addEventListener('blur', () => stopPreview(video), options);
      });
      if (previews.length && 'IntersectionObserver' in window) {
        this.previewObserver = new IntersectionObserver(entries => entries.forEach(entry => { if (!entry.isIntersecting) stopPreview(entry.target); }));
        previews.forEach(video => this.previewObserver.observe(video));
      }
      document.addEventListener('visibilitychange', () => { if (document.hidden) stopPreviews(); }, options);
      // A successful native add must reveal the cart instead of leaving it behind a modal.
      document.addEventListener('ajaxProduct:added', () => {
        this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      }, options);
      reduced.addEventListener('change', () => { if (reduced.matches) stopPreviews(); }, options);
      navigator.connection?.addEventListener('change', () => { if (navigator.connection.saveData) stopPreviews(); }, options);
      this.stopPreviews = stopPreviews;
      const rail = this.querySelector('.dope-lookbook__rail');
      const arrows = this.querySelector('.dope-lookbook__arrows');
      const previous = arrows.querySelector('[data-scroll="-1"]');
      const next = arrows.querySelector('[data-scroll="1"]');
      const updateArrows = () => {
        const maxScroll = rail.scrollWidth - rail.clientWidth;
        arrows.hidden = maxScroll <= 1;
        previous.disabled = rail.scrollLeft <= 1;
        next.disabled = rail.scrollLeft >= maxScroll - 1;
      };
      updateArrows();
      rail.addEventListener('scroll', updateArrows, {...options, passive: true});
      if ('ResizeObserver' in window) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(updateArrows);
        this.resizeObserver.observe(rail);
      }
      rail.addEventListener('keydown', (event) => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        rail.scrollBy({left: (event.key === 'ArrowRight' ? 1 : -1) * rail.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }, options);
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-video-open]');
        if (opener) {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          const dialog = this.querySelector(`#${CSS.escape(opener.dataset.videoOpen)}`);
          if (!dialog?.showModal) return;
          event.preventDefault();
          stopPreviews();
          this.opener = opener;
          dialog.showModal();
          window.dopeClarityEvent?.('dope_lookbook_open');
          const video = dialog.querySelector('video');
          if (video) {
            const source = video.querySelector('source');
            if (!source.src) { source.src = source.dataset.src; video.load(); }
            video.play().catch(() => { /* Native controls remain available. */ });
          }
          const frame = dialog.querySelector('[data-youtube-id]');
          if (frame && /^[\w-]{11}$/.test(frame.dataset.youtubeId)) {
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.youtube-nocookie.com/embed/${frame.dataset.youtubeId}?autoplay=1&rel=0`;
            iframe.title = dialog.querySelector('h3').textContent;
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            iframe.allowFullscreen = true;
            frame.replaceChildren(iframe);
          }
        }
        if (event.target.closest('.dope-video-card__product, .dope-video-dialog .dope-pill')) window.dopeClarityEvent?.('dope_lookbook_shop');
        if (event.target.closest('[data-video-close]')) event.target.closest('dialog').close();
        if (event.target.tagName === 'DIALOG') {
          const bounds = event.target.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.target.close();
        }
        const arrow = event.target.closest('[data-scroll]');
        if (arrow) {
          rail.scrollBy({left: Number(arrow.dataset.scroll) * rail.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }
      }, options);
      this.querySelectorAll('dialog').forEach((dialog) => dialog.addEventListener('close', () => {
        dialog.querySelector('video')?.pause();
        dialog.querySelector('[data-youtube-id]')?.replaceChildren();
        this.opener?.focus({preventScroll: true});
      }, options));
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.stopPreviews?.();
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.querySelectorAll('[data-youtube-id]').forEach((frame) => frame.replaceChildren());
    }
  });
}
